import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useContextSelector } from 'use-context-selector';

import { rootElementSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { preventAlphaInput } from '@/core/utils/helpers';
import { Button } from '@/ux/components/shadcn/ui/button';
import { CountryPicker001 } from '@/ux-legacy/uikit/elements/elements-index';

type UserDetailsCaptureProps = {
  onNext: any;
  setUserData: any;
  userData: any;
};
const defaultCountryPick = {
  name: 'Norway',
  flag: '🇳🇴',
  code: 'NO',
  dial_code: '+47',
};

const UserDetailsCapture: FC<UserDetailsCaptureProps> = ({ onNext, setUserData, userData }) => {
  const [country, OnCountryCodeChange] = useState(defaultCountryPick);
  const rootElement = useContextSelector(AppContext, rootElementSelector);
  const [userDetails, setUserDetails] = useState<any>({
    name: userData?.name,
    phone: userData?.phone,
    email: userData?.email,
  });

  const {
    register,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: userData?.name || '',
      phone: userData?.phone || '',
      email: userData?.email || '',
    },
  });

  useEffect(() => {
    if (userData?.country?.dial_code) {
      OnCountryCodeChange(userData?.country);
    }
  }, []);

  const handleChange = (field: any, value: any) => {
    setUserDetails({ ...userDetails, [field]: value });
  };

  const handleSubmitForm = async () => {
    const valid = await trigger();
    if (valid) {
      const values: any = getValues();
      setUserData({ ...values, country: country });
      onNext();
    }
  };

  return (
    <div className="flex gap-2 flex-col h-full">
      <div className="grow overflow-y-auto">
        <div className="p-2 flex gap-y-2 flex-col">
          <div className="rounded-md border space-y-2 p-1">
            <input
              className="w-full p-2 border-0"
              maxLength={30}
              placeholder="Name"
              {...register('name', {
                required: 'Name is required',
                validate: (value) => {
                  return value.trim() !== '' && !value.startsWith(' ');
                },
              })}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
          {errors.name && (
            <p className="flex justify-between text-slate-400 text-sm">
              <div>{errors.name && <span className="text-red-500">{`Name is Required`}</span>}</div>
            </p>
          )}
          <div className="rounded-md space-y-2 border relative">
            <div className="absolute top-2 left-0 ">
              <CountryPicker001
                onChange={(country: any) => {
                  OnCountryCodeChange(country);
                }}
                rootElement={rootElement}
                selected={country}
                classNames=""
              />
            </div>
            <input
              type="text"
              inputMode="numeric"
              onKeyDown={preventAlphaInput}
              maxLength={10}
              className="border-0 ml-24 w-[75%]"
              placeholder="Phone"
              {...register('phone', {
                required: 'Phone is required',
                validate: (value) => {
                  return value.trim() !== '' && !value.startsWith(' ');
                },
              })}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </div>
          {errors.phone && (
            <p className="flex justify-between text-slate-400 text-sm">
              <div>{errors.phone && <span className="text-red-500">{`Phone is Required`}</span>}</div>
            </p>
          )}
          <div className="rounded-md border space-y-2 p-1">
            <input
              type="email"
              className="border-0 w-full"
              placeholder="Email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'invalid email address',
                },
                validate: (value) => {
                  return value.trim() !== '' && !value.startsWith(' ');
                },
              })}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </div>
          {errors.email && (
            <p className="flex justify-between text-slate-400 text-sm">
              <div>{errors.email && <span className="text-red-500">{`Invalid/Email is Required`}</span>}</div>
            </p>
          )}
        </div>
      </div>
      <div className="flex justify-end p-2">
        <Button onClick={handleSubmitForm} className="bg-black text-white">
          Next
        </Button>
      </div>
    </div>
  );
};

export default UserDetailsCapture;
